import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import {
  alpha,
  Box,
  Button,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import { EventModalTypes } from "../DialogModalWrapper";
import { DialogModalStyles } from "../DialogModalWrapper";
import { IStatusCheckIssue } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { useDispatch } from "react-redux";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";

export enum PairingErrorTypes {
  OpeningFlyingAssignmentError = "OpeningFlyingAssignmentError",
  AvailablePairingAssignmentError = "AvailablePairingAssignmentError",
}
interface IProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
  modalProps: {
    errors: IStatusCheckIssue[];
    title?: string;
    pairingId: string;
    pairingShortCode: string;
    crewMember: ICrewMember;
    pairingErrorTypes: PairingErrorTypes;
    openFlyingRequest?: string;
  };
}

/**
 *  Modal to alert a user to an error on the pairing
 */
export const PairingErrorModal = (props: IProps): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal, modalProps } = props;
  const { errors, title, pairingId, pairingShortCode, crewMember, pairingErrorTypes, openFlyingRequest } = modalProps;
  const theme = useTheme();
  const dispatch = useDispatch();
  const errorDescriptions = (errors ?? []).map((e) => e?.description).filter(Boolean);
  //////////////
  // HANDLERS //
  //////////////
  const handleCancel = (): void => {
    //cancel modal
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  const handleAssignPairing = async (): Promise<void> => {
    const crewMemberName = `${crewMember.firstName.substring(0, 1)}. ${crewMember.lastName}`;
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `Assigning Pairing to ${crewMemberName}`,
      successMessage: `Pairing Assignment Successful for ${crewMemberName}`,
      errorMessage: `Error: Unable to Assign Pairing to ${crewMemberName}`,
    };

    const response: any = await AmplifyAPIWrapper.Put(
      `/assignCrewToPairing/${pairingId}/crewId/${crewMember._id}?ignoreErrors=true`,
      null,
      notifications
    );

    if (response) {
      handleClose(false);
      setActiveEventModal(EventModalTypes.None);
    }
  };

  const handleAssignOpenFlyingPairing = async (): Promise<void> => {
    const crewMemberName = `${crewMember.firstName.substring(0, 1)}. ${crewMember.lastName}`;
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `Assigning Open Flying Request to ${crewMemberName}`,
      successMessage: `Open Flying Request Successfully Assigned to ${crewMemberName}`,
      errorMessage: `Error: Unable to Assign Open Flying Request to ${crewMemberName}`,
    };
    const response = await AmplifyAPIWrapper.Put<any>(
      `/pairings/openflying/assign/${openFlyingRequest}?ignoreErrors=true`,
      null,
      notifications
    );

    if (response) {
      handleClose(false);
      setActiveEventModal(EventModalTypes.None);
    }
  };
  if (isOpen) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content" style={{ maxWidth: 560, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              position: "relative",
              py: 2,
              mb: 1,
              px: 2,
            }}
          >
            <Typography className="title" sx={{ px: 1, py: 0.5 }}>
              {title ?? "Pairing Error"}
            </Typography>
            <ShortCodeChip
              code={pairingShortCode}
              itemId={pairingId}
              type="pairing"
              disableContext
              disableFocus
              style={{ margin: 10, position: "absolute", right: 8 }}
            />
          </Box>

          <Typography sx={{ px: 2, pb: 2, color: theme.palette.text.secondary, fontSize: 13 }}>
            This pairing has errors. You can still proceed by clicking Assign.
          </Typography>

          <Box
            sx={{
              mx: 2,
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              maxHeight: 260,
              overflow: "auto",
              backgroundColor: alpha(theme.palette.action.hover, 0.25),
            }}
          >
            <List disablePadding dense>
              {errorDescriptions.map((desc, index) => (
                <ListItem
                  key={`${pairingId}-issue-${index}`}
                  sx={{
                    px: 2,
                    py: 0.75,
                    "&:not(:last-child)": { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}` },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 28,
                      color: theme.palette.warning.main,
                      alignSelf: "flex-start",
                      mt: 0.25,
                    }}
                  >
                    <WarningAmberOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={desc}
                    primaryTypographyProps={{ variant: "body2", sx: { lineHeight: 1.35 } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <DialogActions sx={{ px: 2, pt: 2.5, justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={handleCancel} color="primary">
              Close
            </Button>
            {pairingErrorTypes === PairingErrorTypes.AvailablePairingAssignmentError && (
              <Button onClick={handleAssignPairing} color="primary" variant="contained">
                Assign
              </Button>
            )}
            {pairingErrorTypes === PairingErrorTypes.OpeningFlyingAssignmentError && (
              <Button onClick={handleAssignOpenFlyingPairing} color="primary" variant="contained">
                Assign
              </Button>
            )}
          </DialogActions>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
